import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CreditCard, DollarSign, Scale, CalendarClock, Ban, Check, AlertTriangle, ArrowLeft, ArrowRight,
  History, X,
} from 'lucide-react';
import { hardwareApi, money, type DeviceBundleRow, type DeviceModelRow } from '../../services/hardwareApi';
import { billingApi, type BillingSkuRow } from '../../services/billingApi';
import { ACTION_BLOCKS_V1, actionBlockById, blocksForEnforcement, enforcementForBlocks } from '@platform/shared-types';
import SearchableDropdown from '../../components/SearchableDropdown';
import ShimmerImage from '../../components/ShimmerImage';
import { InventoryPage, RUBY, RUBY_DEEP } from './InventoryChrome';
import { toastService } from '../../services/toastService';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * FINANCING WIZARD — mockup screen 5, extended by the rental-option mockup
 * (`mockups/rental-option-financing.html`). `/inventory/financing/new[?target=bundle:<id>|model:<id>]`.
 *
 * Five steps: Device -> Sale modes -> Terms -> Agreement -> Review.
 * Review creates ONE billing SKU per enabled sale mode via `billingApi.upsertSku` — the SAME
 * endpoint `OffersTab.tsx` already uses, so an offer created here is indistinguishable from one
 * created there once saved. This wizard SUPERSEDES that tab's own creation form (see OffersTab's
 * "New offer" button, which now navigates here); the tab remains the read/edit list.
 *
 * Three sale modes — BUY (outright), FINANCE (installment) and RENT (rental) — as independent
 * TOGGLES (any combination), plus a "Not sellable" switch that disables all three (no SKU is
 * created; this is how "no offer" is expressed). This replaces the original four fixed policy
 * cards; a saved draft from that era migrates in `readDraft`:
 *   Sale only      -> BUY
 *   Sale + Finance -> BUY + FINANCE
 *   Finance only   -> FINANCE
 *   Not sellable   -> the switch
 *
 * RENT terms come in two kinds: OPEN-ENDED (bills monthly until cancelled, optional minimum
 * commitment) and FIXED TERM (ends at month N — return or renew). Both may carry a refundable
 * security deposit, collected once at checkout (billing-service mints a second one-time Stripe
 * price for it) and tracked on the unit's agreement.
 */

type ModeKey = 'outright' | 'installment' | 'rental';
type RentalKind = 'open_ended' | 'fixed_term';
/**
 * The last step of the wizard. Named rather than inlined because the number
 * changed when the Agreement step moved to the deal (2026-08-30), and a resumed
 * draft can still carry the old one — see `resumeDraft`.
 */
const LAST_STEP = 4;
type Enforcement = 'suspend_all' | 'suspend_feature' | 'collections_only';

const ALL_MODES: ModeKey[] = ['outright', 'installment', 'rental'];

/**
 * Every picker on this page is a SearchableDropdown, never a native <select>.
 * Native selects cannot carry a SUBTITLE, and on this screen the subtitle is
 * where the actual meaning lives — what an enforcement policy does to a
 * customer, what a commission basis pays a partner. They also render as the
 * OS widget inside the iOS webview, which this admin is viewed in.
 *
 * "On default" is the one exception (Task 10): it is now a MULTI-SELECT over
 * `ACTION_BLOCKS_V1`, not a single-value dropdown — see `ActionBlockMultiSelect`
 * below. `SearchableDropdown` has no multi-select mode (`value`/`onChange` are
 * single `string | null`), and with exactly five fixed options a checkbox list
 * is clearer than shoehorning multi-select into a component built for search.
 * The old `ENFORCEMENT_OPTIONS` single-value list it replaced is gone; the
 * legacy `enforcement` enum itself is untouched — see `enforcementActions` on
 * `BillingSkuSchema`.
 */

/**
 * One INSTALLMENT term option — a policy may offer several (24, 36, 60, 72…),
 * each with its own monthly price; ONE SKU is created per option so the
 * customer picks between real offers at checkout.
 */
interface TermRow {
  termMonths: string;
  monthlyDollars: string;
  resellerMonthlyDollars: string;
  downPaymentDollars: string;
  /**
   * EDIT MODE ONLY (2026-08-27): the SKU this row already IS.
   *
   * `skuId` carries `Math.random()` in its suffix, so it can never be
   * re-derived — an edit that regenerated it would mint a new SKU and orphan
   * every agreement billing against the old one. A row seeded from an
   * existing offer therefore carries its id here and updates in place;
   * a row the operator adds has none and mints a new SKU on save.
   */
  skuId?: string;
}

const blankTermRow = (termMonths = '24'): TermRow => ({
  termMonths, monthlyDollars: '', resellerMonthlyDollars: '', downPaymentDollars: '0',
});

interface ModeTerms {
  retailDollars: string;         // outright: one-time price. rental: monthly price.
  resellerDollars: string;       // outright: reseller price.
  resellerMonthlyDollars: string; // rental
  termMonths: string;            // fixed-term rental (installment terms live in termRows)
  downPaymentDollars: string;    // legacy single-term installment field (migrated into termRows)
  graceDays: string;             // installment + rental
  enforcement: Enforcement;      // installment + rental
  /**
   * Task 10 — "on default" as a list of action block ids. ADDITIVE beside
   * `enforcement`, which the wizard keeps writing (derived via
   * `enforcementForBlocks`) so a reader that only knows the old field never
   * meets a value it cannot parse. Absent/undefined until the operator
   * touches the multi-select, or when seeded from a SKU that predates it —
   * `blocksForEnforcement(enforcement)` is the fallback everywhere this is read.
   */
  enforcementActions?: string[]; // installment + rental
  warrantyMonths: string;
  /*
    NO COMMISSION, NO AGREEMENT, NO MARKETPLACE FLAG (2026-08-30).

    `commissionMode` / `commissionPercent` / `commissionPlanId` /
    `agreementKey` / `agreementDocumentIds` / `marketplace` all used to live
    here. Commission and documents are the DEAL's now, and "is it on the shelf"
    is answered once when the device is created — reaching this wizard is what
    sellable means. The SKU fields themselves are untouched: old offers keep
    theirs, and checkout still reads them as the fallback.
  */
  rentalKind: RentalKind;        // rental only
  minCommitmentMonths: string;   // rental only (open-ended): '0' = cancel any time
  depositDollars: string;        // rental only: '0' = no deposit
  termRows: TermRow[];           // installment only: one offer per row
  /** EDIT MODE: the existing SKU for a single-SKU mode (outright / rental). Same carry-through rule as `TermRow.skuId`. */
  skuId?: string;
}

const blankTerms = (): ModeTerms => ({
  retailDollars: '', resellerDollars: '', resellerMonthlyDollars: '',
  termMonths: '24', downPaymentDollars: '0', graceDays: '14', enforcement: 'suspend_feature',
  warrantyMonths: '12',
  rentalKind: 'open_ended', minCommitmentMonths: '0', depositDollars: '0',
  termRows: [blankTermRow()],
});

// ---------------------------------------------------------------------------
// EDIT MODE (2026-08-27 financing-policy spec)
//
// A "policy" as an operator thinks of it is A DEVICE AND ITS OFFERS — which is
// exactly the shape this wizard already models (modes × terms), and exactly
// what the old flat edit modal could not express. Editing therefore seeds the
// wizard from EVERY device_finance SKU pointing at the same model/bundle, and
// step 1 (Device) is locked: changing which device an existing offer belongs
// to would silently re-point live agreements.
//
// Only the three modes this wizard models are seeded. A `lease` or `included`
// SKU (creatable from the Offers tab) has no representation here, so it is
// reported as untouched rather than silently dropped — see `unsupportedSkus`.
// ---------------------------------------------------------------------------
const centsToDollarStr = (c?: number): string =>
  typeof c === 'number' && c > 0 ? (c / 100).toFixed(2) : '';

/** Shared per-mode settings read back off any one SKU of that mode. */
const commonTermsFromSku = (sku: BillingSkuRow, into: ModeTerms): void => {
  into.graceDays = String(sku.defaultPolicy?.graceDays ?? 14);
  into.enforcement = sku.defaultPolicy?.enforcement || (sku.enforcement as Enforcement) || 'suspend_feature';
  // Additive field — `BillingSkuRow` (billingApi.ts) predates it, so read it
  // defensively. Absent (every SKU stored before Task 10) leaves this
  // undefined and every reader below falls back to `blocksForEnforcement`.
  into.enforcementActions = (sku as any).enforcementActions;
  into.warrantyMonths = sku.warrantyMonths != null ? String(sku.warrantyMonths) : '';
  // Commission, agreements and the marketplace flag are deliberately NOT read
  // back: the wizard no longer edits them, and seeding a field nothing renders
  // would only make a stale value look like a current one.
};

interface SeededPolicy {
  targetKind: 'bundle' | 'model';
  targetId: string;
  modes: Record<ModeKey, boolean>;
  terms: Record<ModeKey, ModeTerms>;
  /** SKUs on this device the wizard cannot represent (lease/included) — left alone on save. */
  unsupportedSkus: BillingSkuRow[];
  /** Retired SKUs seeded back in, so re-offering is an edit rather than a re-create. */
  retiredCount: number;
}

/**
 * Rebuilds wizard state from the SKUs of one device. `skus` should already be
 * filtered to `type === 'device_finance'`.
 */
export function seedPolicyFromSkus(
  skus: BillingSkuRow[],
  targetKind: 'bundle' | 'model',
  targetId: string,
): SeededPolicy {
  const mine = skus.filter((s) => (targetKind === 'bundle' ? s.bundleId === targetId : s.modelId === targetId));
  const terms: Record<ModeKey, ModeTerms> = {
    outright: blankTerms(), installment: blankTerms(), rental: blankTerms(),
  };
  const modes: Record<ModeKey, boolean> = { outright: false, installment: false, rental: false };
  const unsupportedSkus: BillingSkuRow[] = [];
  let retiredCount = 0;

  const installmentSkus = mine
    .filter((s) => s.saleMode === 'installment')
    .sort((a, b) => (a.termMonths || 0) - (b.termMonths || 0));

  for (const sku of mine) {
    if (sku.status !== 'active') retiredCount++;
    if (sku.saleMode === 'outright') {
      modes.outright = true;
      terms.outright.skuId = sku.skuId;
      terms.outright.retailDollars = centsToDollarStr(sku.amountCents);
      terms.outright.resellerDollars = centsToDollarStr(sku.resellerPriceCents);
      commonTermsFromSku(sku, terms.outright);
    } else if (sku.saleMode === 'rental') {
      modes.rental = true;
      terms.rental.skuId = sku.skuId;
      terms.rental.retailDollars = centsToDollarStr(sku.amountCents);
      terms.rental.resellerMonthlyDollars = centsToDollarStr(sku.resellerMonthlyCents);
      terms.rental.rentalKind = sku.rentalKind || 'open_ended';
      terms.rental.termMonths = sku.termMonths ? String(sku.termMonths) : '24';
      terms.rental.minCommitmentMonths = String(sku.minCommitmentMonths ?? 0);
      terms.rental.depositDollars = sku.depositCents ? (sku.depositCents / 100).toFixed(2) : '0';
      commonTermsFromSku(sku, terms.rental);
    } else if (sku.saleMode === 'installment') {
      modes.installment = true;
    } else {
      // lease / included / unset — not modelled by this wizard.
      unsupportedSkus.push(sku);
    }
  }

  if (installmentSkus.length) {
    terms.installment.termRows = installmentSkus.map((s) => ({
      skuId: s.skuId,
      termMonths: s.termMonths ? String(s.termMonths) : '',
      monthlyDollars: centsToDollarStr(s.amountCents),
      resellerMonthlyDollars: centsToDollarStr(s.resellerMonthlyCents),
      downPaymentDollars: s.downPaymentCents ? (s.downPaymentCents / 100).toFixed(2) : '0',
    }));
    // Shared settings come off the FIRST term — they are written identically
    // to every installment SKU on save, so any one of them is representative.
    commonTermsFromSku(installmentSkus[0], terms.installment);
  }

  return { targetKind, targetId, modes, terms, unsupportedSkus, retiredCount };
}

const MODE_CARDS: { id: ModeKey; icon: React.FC<any>; badge: string; name: string; desc: string }[] = [
  { id: 'outright', icon: DollarSign, badge: 'BUY', name: 'Sell outright', desc: 'Customer pays once, owns it day one. Best for cheap peripherals — printers, scanners.' },
  { id: 'installment', icon: Scale, badge: 'FINANCE', name: 'Installments', desc: 'Fixed monthly over a term; customer owns it at the end. The standard register play.' },
  { id: 'rental', icon: CalendarClock, badge: 'RENT', name: 'Rent it', desc: 'Monthly rent, device stays yours. Open-ended until cancelled, or a fixed rental term — returned on cancel.' },
];

const MODE_BADGE: Record<ModeKey, { label: string; cls: string }> = {
  outright: { label: 'BUY', cls: 'bg-blue-100 text-blue-700' },
  installment: { label: 'FINANCE', cls: 'bg-rose-100 text-rose-700' },
  rental: { label: 'RENT', cls: 'bg-teal-100 text-teal-700' },
};

const toCents = (s: string): number => {
  const n = Number.parseFloat(s);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
};
const toInt = (s: string, fallback = 0): number => {
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

const slugify = (s: string) =>
  (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'dev';

// ---------------------------------------------------------------------------
// Draft persistence — the wizard autosaves to localStorage so an operator can
// leave at ANY step and resume without re-entering everything. Per-browser
// convenience only (a draft is pre-decision pricing input, not operational
// state); cleared the moment the policy is actually created. Every access is
// try/catch-wrapped — a blocked/absent localStorage just means no drafts.
// ---------------------------------------------------------------------------

const DRAFT_KEY = 'rt_inventory_financing_wizard_draft';

interface WizardDraft {
  step: number;
  targetKind: 'bundle' | 'model' | '';
  targetId: string;
  modes: Record<ModeKey, boolean>;
  notSellable: boolean;
  terms: Record<ModeKey, ModeTerms>;
  savedAt: number;
}

/** The pre-rental four-policy vocabulary, kept ONLY to migrate old drafts. */
const modesFromLegacyPolicy = (policy: string): { modes: Record<ModeKey, boolean>; notSellable: boolean } => ({
  modes: {
    outright: policy === 'sale_only' || policy === 'sale_finance',
    installment: policy === 'sale_finance' || policy === 'finance_only',
    rental: false,
  },
  notSellable: policy === 'not_sellable',
});

const readDraft = (): WizardDraft | null => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || typeof d.step !== 'number' || !d.terms) return null;
    // A draft saved before the rent option carried `policy` instead of
    // modes/notSellable — migrate it rather than discarding the operator's
    // half-entered pricing.
    const legacy = typeof d.policy === 'string' && !d.modes ? modesFromLegacyPolicy(d.policy) : null;
    // Merge over blanks so a draft saved before a ModeTerms field existed
    // still loads with sane defaults instead of undefined-poisoned inputs.
    // A draft saved before multi-term options carried a SINGLE installment
    // term in retailDollars/termMonths — carry it into termRows[0] rather
    // than discarding the operator's pricing.
    const installmentDraft: ModeTerms = { ...blankTerms(), ...(d.terms.installment || {}) };
    if (!Array.isArray(d.terms.installment?.termRows) || d.terms.installment.termRows.length === 0) {
      installmentDraft.termRows = [{
        termMonths: installmentDraft.termMonths || '24',
        monthlyDollars: installmentDraft.retailDollars || '',
        resellerMonthlyDollars: installmentDraft.resellerMonthlyDollars || '',
        downPaymentDollars: installmentDraft.downPaymentDollars || '0',
      }];
    } else {
      installmentDraft.termRows = d.terms.installment.termRows.map((r: any) => ({ ...blankTermRow(), ...r }));
    }
    return {
      step: Math.min(Math.max(1, d.step), 5),
      targetKind: d.targetKind === 'bundle' || d.targetKind === 'model' ? d.targetKind : '',
      targetId: typeof d.targetId === 'string' ? d.targetId : '',
      modes: legacy ? legacy.modes : {
        outright: !!d.modes?.outright,
        installment: !!d.modes?.installment,
        rental: !!d.modes?.rental,
      },
      notSellable: legacy ? legacy.notSellable : !!d.notSellable,
      terms: {
        outright: { ...blankTerms(), ...(d.terms.outright || {}) },
        installment: installmentDraft,
        rental: { ...blankTerms(), ...(d.terms.rental || {}) },
      },
      savedAt: typeof d.savedAt === 'number' ? d.savedAt : Date.now(),
    };
  } catch {
    return null;
  }
};

const writeDraft = (d: WizardDraft) => { try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* storage unavailable — drafts just don't persist */ } };
const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };

const WizStep: React.FC<{ n: number; label: string; current: number }> = ({ n, label, current }) => {
  const done = n < current;
  const now = n === current;
  return (
    <div className="flex items-center gap-2 text-xs whitespace-nowrap">
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border ${
          now ? 'text-white border-transparent' : done ? 'border-red-200' : 'border-stone-300 bg-white text-stone-400'
        }`}
        style={now ? { background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` } : done ? { background: '#fde7ec', color: RUBY } : undefined}
      >
        {done ? <Check size={12} /> : n}
      </div>
      <span className={now ? 'font-semibold text-stone-900' : done ? 'text-stone-500' : 'text-stone-400'}>{label}</span>
    </div>
  );
};

const NumberField: React.FC<{
  label: string; value: string; onChange: (v: string) => void; prefix?: string; suffix?: string;
  readOnly?: boolean; hint?: string; step?: string;
}> = ({ label, value, onChange, prefix, suffix, readOnly, hint, step = '0.01' }) => (
  <div>
    <label className="mb-1 block text-[11px] font-semibold text-stone-600">{label}</label>
    <div className="flex items-center gap-1.5">
      {prefix && <span className="text-xs text-stone-400">{prefix}</span>}
      <input
        type="number" min="0" step={step} value={value} readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className={`w-28 rounded-lg border px-2.5 py-1.5 text-xs font-mono tabular-nums text-right ${
          readOnly ? 'border-stone-200 bg-stone-100 text-stone-500' : 'border-stone-300 bg-white text-stone-900'
        }`}
      />
      {suffix && <span className="text-xs text-stone-400">{suffix}</span>}
    </div>
    {hint && <p className="mt-1 text-[10.5px] text-emerald-600">{hint}</p>}
  </div>
);

/**
 * "On default" — a MULTI-SELECT of `ACTION_BLOCKS_V1` (Task 10). Built locally
 * rather than a `SearchableDropdown` mode: that component's `value`/`onChange`
 * are single `string | null` (see its definition), and five fixed options read
 * more clearly as a checkbox list than as a dropdown anyway. Same selected-state
 * styling as the sale-mode cards in step 2 (RUBY border/fill + check chip).
 */
const ActionBlockMultiSelect: React.FC<{
  value: readonly string[];
  onChange: (ids: string[]) => void;
}> = ({ value, onChange }) => (
  <div className="space-y-1.5">
    {ACTION_BLOCKS_V1.map((b) => {
      const on = value.includes(b.id);
      return (
        <button
          key={b.id}
          type="button"
          onClick={() => onChange(on ? value.filter((id) => id !== b.id) : [...value, b.id])}
          className={`flex w-full items-start gap-2.5 rounded-lg border p-2 text-left transition ${
            on ? '' : 'border-stone-200 bg-white hover:border-stone-300'
          }`}
          style={on ? { borderColor: RUBY, background: '#fef2f4' } : undefined}
        >
          <div
            className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
              on ? 'border-transparent text-white' : 'border-stone-300 text-transparent'
            }`}
            style={on ? { background: RUBY } : undefined}
          >
            <Check size={10} />
          </div>
          <div>
            <div className="text-[11px] font-semibold text-stone-900">{b.label}</div>
            <div className="text-[10px] text-stone-500">{b.description}</div>
          </div>
        </button>
      );
    })}
  </div>
);

/** Review-step label for a selected block list — e.g. "Suspend features + Collections only". */
const blockLabels = (ids: readonly string[]): string =>
  ids.length ? ids.map((id) => actionBlockById(id)?.label ?? id).join(' + ') : 'None selected';

const FinancingWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [bundles, setBundles] = useState<DeviceBundleRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [step, setStep] = useState(1);
  const [targetKind, setTargetKind] = useState<'bundle' | 'model' | ''>('');
  const [targetId, setTargetId] = useState('');
  // BUY + FINANCE on by default — the old "Sale + Finance" standard. RENT is
  // deliberately opt-in per offer.
  const [modes, setModes] = useState<Record<ModeKey, boolean>>({ outright: true, installment: true, rental: false });
  const [notSellable, setNotSellable] = useState(false);
  const [terms, setTerms] = useState<Record<ModeKey, ModeTerms>>({ outright: blankTerms(), installment: blankTerms(), rental: blankTerms() });

  // ---- EDIT MODE (2026-08-27) ----------------------------------------
  // `?edit=bundle:<id>` / `?edit=model:<id>` opens this wizard on an
  // EXISTING policy. Everything below behaves identically to create except
  // where `isEdit` is consulted: the device step is locked, the draft is
  // keyed per-policy so an edit can never overwrite a half-finished create,
  // and save reconciles against the SKUs seeded here.
  const editTargetRaw = searchParams.get('edit') || '';
  const isEdit = editTargetRaw.includes(':');
  const [seeded, setSeeded] = useState<SeededPolicy | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);
  /** skuIds present when the policy was loaded, so save can spot removals. */
  const [seededSkuIds, setSeededSkuIds] = useState<string[]>([]);
  /** The stored rows, by id — used to force a Stripe re-mint only when an amount actually moved. */
  const [seededById, setSeededById] = useState<Record<string, BillingSkuRow>>({});
  const [retiredSkuIds, setRetiredSkuIds] = useState<string[]>([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [created, setCreated] = useState<string[] | null>(null);
  // W5 review I6 — per-SKU Stripe price generation result, so the operator is
  // told plainly (not silently) which offers are actually sellable.
  const [priceResults, setPriceResults] = useState<Record<string, { ok: boolean; message: string }>>({});
  /** Publishing the device this wizard was opened from — see `publishDevice`. */
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Unfinished draft from a previous visit — offered, never silently applied.
  // Not offered while editing: an edit's starting point is the LIVE policy,
  // and resuming a create-draft over it would silently mix two policies.
  const [pendingDraft, setPendingDraft] = useState<WizardDraft | null>(() => (
    (new URLSearchParams(window.location.search).get('edit') || '').includes(':') ? null : readDraft()
  ));
  /** skuId -> the name it was saved with, so the result reads in words. */
  const [createdNames, setCreatedNames] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [b, m] = await Promise.all([hardwareApi.listBundles(), hardwareApi.listModels()]);
        if (cancelled) return;
        setBundles(b.items || []);
        setModels(m.items || []);

        // EDIT (2026-08-27): seed every mode and term from the device's own
        // SKUs, then open on step 2 with step 1 locked.
        if (isEdit) {
          const [kind, id] = editTargetRaw.split(':') as ['bundle' | 'model', string];
          if (kind !== 'bundle' && kind !== 'model') {
            setSeedError(`"${editTargetRaw}" is not a policy this page can open.`);
          } else {
            try {
              const res = await billingApi.listSkus();
              if (cancelled) return;
              const deviceSkus = (res.items || []).filter((s) => s.type === 'device_finance');
              const seed = seedPolicyFromSkus(deviceSkus, kind, id);
              const mine = deviceSkus.filter((s) => (kind === 'bundle' ? s.bundleId === id : s.modelId === id));
              if (!mine.length) {
                setSeedError('This device has no financing offers to edit yet.');
              } else {
                setSeeded(seed);
                setSeededSkuIds(mine.map((s) => s.skuId));
                setSeededById(Object.fromEntries(mine.map((s) => [s.skuId, s])));
                setTargetKind(seed.targetKind);
                setTargetId(seed.targetId);
                setModes(seed.modes);
                setTerms(seed.terms);
                setStep(2);
              }
            } catch (e: any) {
              if (!cancelled) setSeedError(e?.message || 'Could not load this policy’s existing offers.');
            }
          }
        } else {
          const raw = searchParams.get('target');
          if (raw && raw.includes(':')) {
            const [kind, id] = raw.split(':') as ['bundle' | 'model', string];
            if (kind === 'bundle' || kind === 'model') {
              setTargetKind(kind);
              setTargetId(id);
              setStep(2); // device preselected — jump straight to Selling policy, per the mockup
            }
          }
        }
      } catch {
        // bundles/models failing to load is surfaced by the empty device picker below
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave — every meaningful change persists, so leaving at any step is
  // safe. Skips the pristine state (nothing worth resuming) and clears once
  // the policy is created (nothing left to resume).
  useEffect(() => {
    if (loading || created) return;
    // An edit is never drafted to the create key. The starting point of an
    // edit is the live policy, so a half-finished edit must not resurface as
    // "resume your unfinished offer" the next time someone creates one.
    if (isEdit) return;
    if (step === 1 && !targetId) return;
    writeDraft({ step, targetKind, targetId, modes, notSellable, terms, savedAt: Date.now() });
  }, [step, targetKind, targetId, modes, notSellable, terms, loading, created, isEdit]);

  const resumeDraft = () => {
    if (!pendingDraft) return;
    setTargetKind(pendingDraft.targetKind);
    setTargetId(pendingDraft.targetId);
    setModes(pendingDraft.modes);
    setNotSellable(pendingDraft.notSellable);
    setTerms(pendingDraft.terms);
    // CLAMPED (2026-08-30): the wizard lost a step, so a draft autosaved at the
    // old step 5 (Review) names a step that no longer renders — the operator
    // would resume onto a blank page with no Continue button. Anything past the
    // last step resumes at the last step.
    setStep(Math.min(Math.max(1, pendingDraft.step), LAST_STEP));
    setPendingDraft(null);
  };

  const discardDraft = () => {
    clearDraft();
    setPendingDraft(null);
  };

  const bundle = targetKind === 'bundle' ? bundles.find((b) => b.bundleId === targetId) : undefined;
  const model = targetKind === 'model' ? models.find((m) => m.modelId === targetId) : undefined;
  const targetName = bundle?.name || model?.modelName || '';

  // -------------------------------------------------------------------------
  // COST, BROKEN OUT (2026-08-27).
  //
  // This used to be `bundle?.totalCostCents ?? model?.landedCostCents`, which
  // silently costed the two targets by DIFFERENT rules: a model's landed cost
  // deliberately EXCLUDES outbound shipping (it is per shipment, not per unit —
  // see `landedCostCents` in hardwareCatalog.ts and its test), while a bundle's
  // `totalCostCents` ADDS `combinedOutboundShippingCents`. Same label, two
  // formulas, so a bundle policy's margins read lower than a model policy's for
  // no reason an operator could see.
  //
  // Both now cost the same way — LANDED (unit + inbound + duty), outbound shown
  // as its own line — and `includeOutbound` lets the operator fold outbound in
  // to see the other view. It is a VIEW ONLY: nothing here is saved, so the
  // toggle can never change what a SKU stores.
  // -------------------------------------------------------------------------
  const [includeOutbound, setIncludeOutbound] = useState(false);

  const landedCents = bundle ? bundle.componentsLandedCents : (model?.landedCostCents ?? 0);
  const outboundCents = bundle
    ? (bundle.combinedOutboundShippingCents || 0)
    : (model?.economics?.defaultOutboundShippingCents || 0);
  /** What every margin, payback and spread figure ahead is measured against. */
  const costCents = landedCents + (includeOutbound ? outboundCents : 0);

  const deviceOptions = useMemo(() => [
    // Landed for BOTH kinds, so the subtitle agrees with the card below it.
    // An ARCHIVED bundle (`status === 'eol'`) is not offered for sale, so no new
    // finance option may be written against it — except the one already being
    // edited, which must stay listed or the dropdown would show a blank target.
    ...bundles
      .filter((b) => b.status !== 'eol' || (targetKind === 'bundle' && b.bundleId === targetId))
      .map((b) => ({ value: `bundle:${b.bundleId}`, label: b.name, subtitle: `Bundle · landed ${money(b.componentsLandedCents)}` })),
    ...models.map((m) => ({ value: `model:${m.modelId}`, label: m.modelName, subtitle: `Device model · landed ${money(m.landedCostCents)}` })),
  ], [bundles, models, targetKind, targetId]);

  const enabledModes = notSellable ? [] : ALL_MODES.filter((m) => modes[m]);
  const noOffer = notSellable || enabledModes.length === 0;

  const setMode = (mode: ModeKey, patch: Partial<ModeTerms>) =>
    setTerms((prev) => ({ ...prev, [mode]: { ...prev[mode], ...patch } }));

  const steps = [
    // EDIT: the device is fixed. Re-pointing an existing offer at another
    // device would silently move every live agreement with it, so step 1 is
    // shown as locked rather than made editable.
    { n: 1, label: isEdit ? 'Device (locked)' : 'Device' },
    { n: 2, label: 'Sale modes' },
    { n: 3, label: 'Terms' },
    // AGREEMENT STEP REMOVED 2026-08-30. Documents are chosen on the DEAL now
    // (hardware and software separately, plus the return/refund policy and any
    // operator-named line) — see `pages/deals/DealDocumentsSections.tsx`. An
    // offer could only ever name one set of documents for every customer who
    // ever bought that device, which is not what a negotiated sale looks like.
    { n: 4, label: 'Review' },
  ];

  const step1Valid = !!targetKind && !!targetId;
  const step3Valid = enabledModes.every((m) => {
    const t = terms[m];
    if (m === 'installment') {
      // One offer per term row — every row needs a positive term AND monthly,
      // and duplicate terms would create two indistinguishable offers.
      if (t.termRows.length === 0) return false;
      const termsSeen = new Set<number>();
      for (const row of t.termRows) {
        const term = toInt(row.termMonths);
        if (!(term > 0) || !(toCents(row.monthlyDollars) > 0) || termsSeen.has(term)) return false;
        termsSeen.add(term);
      }
      return true;
    }
    if (!(toCents(t.retailDollars) > 0)) return false;
    if (m === 'rental' && t.rentalKind === 'fixed_term' && !(toInt(t.termMonths) > 0)) return false;
    return true;
  });
  /*
    THE AGREEMENT GATE MOVED WITH THE AGREEMENTS (2026-08-30).

    `step4Valid` used to refuse to leave the Agreement step until every enabled
    mode had a document bound, so a financed offer could not ship with no
    contract. That check is gone from here because the documents are, and it is
    NOT silently dropped: the deal now carries the binder, so the same question
    is asked once per negotiated sale on the deal screen instead of once per
    device offer. Offers created before this still carry their own
    `agreementDocumentIds` and checkout still reads them as the fallback — see
    `dealDocumentsConfigured`.
  */
  const canContinue =
    (step === 1 && step1Valid)
    || (step === 2 && (notSellable || enabledModes.length > 0))
    || (step === 3 && (noOffer || step3Valid));

  /**
   * The SKU bodies one enabled mode creates. Outright and rental stay one
   * offer each; INSTALLMENT creates one offer PER TERM ROW (24/36/60/72…),
   * each with its own monthly price, so the customer has real options to
   * pick between at checkout.
   */
  const buildSkuBodies = (mode: ModeKey): Record<string, any>[] => {
    const t = terms[mode];
    /*
      COMMISSION IS THE DEAL'S NOW (2026-08-30).

      Every offer this wizard writes declares `inherit_deal` and NO plan
      override, which is exactly the first tier of billing-service's cascade
      standing aside: offer override -> partner pin -> deal plan -> global
      default. With the offer silent, the deal's plan is what pays.

      On an EDIT this is written EXPLICITLY rather than omitted, and that is
      deliberate: `upsertSku` merges over the stored row, so an omitted key
      would leave a stale per-offer override in place — one the operator can no
      longer see, because the control that set it is gone. A hidden override
      that outranks the deal is the worst of the three outcomes.
    */
    const affiliateCommission = { mode: 'inherit_deal' as const };
    const warrantyMonths = toInt(t.warrantyMonths, 0) || undefined;

    const modeSlug = mode === 'outright' ? 'buy' : mode === 'installment' ? 'finance' : 'rent';
    const makeBase = (name: string, skuIdExtra = '', existingSkuId?: string): Record<string, any> => ({
      // EDIT (2026-08-27): reuse the STORED id when this offer already exists.
      // `skuId` carries a random suffix and can never be re-derived — a
      // regenerated id would mint a second SKU and orphan every agreement
      // still billing against the original.
      skuId: existingSkuId || `dev_${slugify(targetId)}_${modeSlug}${skuIdExtra}_${Math.random().toString(36).slice(2, 6)}`,
      type: 'device_finance',
      scope: 'tenant',
      name,
      saleMode: mode,
      [targetKind === 'bundle' ? 'bundleId' : 'modelId']: targetId,
      // Required-with-no-default fields for a brand-new SKU — same convention
      // as OffersTab.tsx. NEVER sent on an update: `upsertSku` merges, so a
      // blank pair here would wipe the live Stripe price on every save. A
      // re-mint is forced further down, and only when the amount moved.
      ...(existingSkuId ? {} : { stripePriceId: { dev: '', prod: '' } }),
      cancellable: true,
      // Also re-offers a retired mode that an edit switched back on.
      status: 'active',
      displayOrder: 100,
      affiliateCommission,
      // '' means inherit. ALWAYS sent now (2026-08-30) — see affiliateCommission
      // above: an offer no longer carries a plan of its own, and an edit has to
      // clear any that a previous version of this wizard set.
      commissionPlanId: '',
      // EDIT writes the field even when empty (as 0) so CLEARING actually
      // clears: `upsertSku` merges over the stored row, so an omitted key
      // silently keeps the old value — the exact reason the old edit modal
      // could never unset a reseller price or a warranty. CREATE keeps its
      // original omit-when-empty behaviour so new rows stay unchanged.
      ...(existingSkuId
        ? { warrantyMonths: warrantyMonths || 0 }
        : (warrantyMonths ? { warrantyMonths } : {})),
      /*
        DOCUMENTS AND THE SHELF, BOTH MOVED (2026-08-30).

        `agreementDocumentIds` / `agreementTemplateId` are NOT written here any
        more, and deliberately NOT cleared either. Documents are chosen on the
        deal now, but offers created before this still carry their own list and
        live envelopes were signed against it — `upsertSku` merges, so omitting
        the keys leaves that history intact and checkout keeps reading it as the
        fallback (`dealDocumentsConfigured`). Clearing them would silently
        unbind agreements that are currently in force.

        `marketplace` is TRUE for everything this wizard writes. The per-offer
        checkbox is gone because reaching this wizard at all is what "sellable"
        now means: the device page asks the question once, and an item answered
        "stock only" never gets here.
      */
      marketplace: true,
    });

    /** See makeBase: on an update, an empty money field is written as 0 rather than omitted. */
    const putMoney = (body: Record<string, any>, key: string, cents: number, existingSkuId?: string) => {
      if (cents > 0) body[key] = cents;
      else if (existingSkuId) body[key] = 0;
    };

    if (mode === 'installment') {
      return t.termRows.map((row) => {
        const term = toInt(row.termMonths);
        const body = makeBase(`${targetName} — ${term}-month installment`, `_${term}m`, row.skuId);
        body.amountCents = toCents(row.monthlyDollars);
        body.interval = 'month';
        body.termMonths = term;
        body.financeType = 'installment';
        body.earlyPayoff = 'remaining_no_penalty';
        // Task 10 — write BOTH: `enforcementActions` is the list the multi-select
        // edited, `enforcement` is derived from it via `enforcementForBlocks` so a
        // reader that only knows the old field never meets a value it cannot parse.
        const installmentBlocks = t.enforcementActions ?? blocksForEnforcement(t.enforcement);
        body.enforcementActions = installmentBlocks;
        body.defaultPolicy = { graceDays: toInt(t.graceDays, 14), enforcement: enforcementForBlocks(installmentBlocks) };
        body.enforcement = enforcementForBlocks(installmentBlocks);
        putMoney(body, 'resellerMonthlyCents', toCents(row.resellerMonthlyDollars), row.skuId);
        putMoney(body, 'downPaymentCents', toCents(row.downPaymentDollars), row.skuId);
        return body;
      });
    }

    const base = makeBase(`${targetName} — ${
      mode === 'outright' ? 'Outright'
      : t.rentalKind === 'fixed_term' ? `${t.termMonths}-month rental` : 'Monthly rental'
    }`, '', t.skuId);

    if (mode === 'outright') {
      base.amountCents = toCents(t.retailDollars);
      base.interval = 'once';
      base.enforcement = 'suspend_all'; // an outright sale has nothing to enforce mid-term; kept for schema completeness
      putMoney(base, 'resellerPriceCents', toCents(t.resellerDollars), t.skuId);
    } else if (mode === 'rental') {
      // RENT — monthly until cancelled (open-ended) or a fixed rental term.
      // The device stays ours: no down payment, no early payoff — a rental
      // has no balance to pay off, only a subscription to end.
      base.amountCents = toCents(t.retailDollars); // monthly rent
      base.interval = 'month';
      base.financeType = 'rental';
      base.rentalKind = t.rentalKind;
      if (t.rentalKind === 'fixed_term') {
        base.termMonths = toInt(t.termMonths);
      } else if (toInt(t.minCommitmentMonths) > 0) {
        base.minCommitmentMonths = toInt(t.minCommitmentMonths);
      } else if (t.skuId) {
        base.minCommitmentMonths = 0;
      }
      putMoney(base, 'depositCents', toCents(t.depositDollars), t.skuId);
      // Task 10 — same both-fields write as the installment branch above.
      const rentalBlocks = t.enforcementActions ?? blocksForEnforcement(t.enforcement);
      base.enforcementActions = rentalBlocks;
      base.defaultPolicy = { graceDays: toInt(t.graceDays, 14), enforcement: enforcementForBlocks(rentalBlocks) };
      base.enforcement = enforcementForBlocks(rentalBlocks);
      putMoney(base, 'resellerMonthlyCents', toCents(t.resellerMonthlyDollars), t.skuId);
    }
    return [base];
  };

  /**
   * A Stripe price is immutable, so a changed amount needs a NEW one. Blanking
   * `stripePriceId` is how `generatePrice` is told to mint it (the same lever
   * the old edit modal pulled). Done ONLY when the amount actually moved —
   * blanking on every save would leave the offer unsellable for the window
   * between the write and a successful re-mint, for no reason.
   */
  const forceRemintWhereAmountChanged = (bodies: Record<string, any>[]): Record<string, any>[] =>
    bodies.map((body) => {
      const prior = seededById[body.skuId];
      if (!prior) return body;
      const amountMoved = typeof body.amountCents === 'number' && body.amountCents !== prior.amountCents;
      const depositMoved = (body.depositCents ?? 0) !== (prior.depositCents ?? 0);
      if (!amountMoved && !depositMoved) return body;
      return {
        ...body,
        ...(amountMoved ? { stripePriceId: { dev: '', prod: '' } } : {}),
        ...(depositMoved ? { stripeDepositPriceId: { dev: '', prod: '' } } : {}),
      };
    });

  const handleCreate = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const skuIds: string[] = [];
      // The name each offer was saved WITH — "All in One POS Terminal — Rent",
      // not `dev_tc_touch_k10_white_dual_touch_rent_igsc`. The id is a key;
      // reporting a completed job in keys makes an operator decode their own
      // success message to find out what they just created.
      const names: Record<string, string> = {};
      for (const mode of enabledModes) {
        for (const body of forceRemintWhereAmountChanged(buildSkuBodies(mode))) {
          const res = await billingApi.upsertSku(body);
          skuIds.push(res.sku.skuId);
          names[res.sku.skuId] = String(res.sku.name || body.name || res.sku.skuId);
        }
      }
      setCreatedNames(names);

      // EDIT reconciliation (2026-08-27): an offer that was on the policy
      // when it opened but is not in what we just wrote — a term row the
      // operator removed, or a whole mode switched off — is RETIRED, never
      // deleted. Agreements already billing against it keep running; it just
      // stops being offered. Deleting would break the financing book's
      // name/term join for those live agreements.
      const retired: string[] = [];
      if (isEdit) {
        const written = new Set(skuIds);
        for (const skuId of seededSkuIds) {
          if (written.has(skuId)) continue;
          try {
            await billingApi.upsertSku({ skuId, status: 'deprecated' });
            retired.push(skuId);
          } catch (e: any) {
            // Reported, never fatal: the offers that WERE saved are already
            // live, so failing the whole edit here would misreport them.
            console.error('[financing edit] could not retire', skuId, e);
            setSaveError(`Saved, but "${skuId}" could not be retired — retire it from the policy list.`);
          }
        }
        setRetiredSkuIds(retired);
      }

      setCreated(skuIds);
      clearDraft(); // done — nothing left to resume

      // W5 review I6 — a wizard-created SKU has `stripePriceId: { dev: '',
      // prod: '' }`; nothing bills until a real price exists, and the FIRST
      // customer to pick the offer used to hit a hard checkout failure. Mint
      // the price right here, and tell the operator per-SKU whether it
      // actually worked — never silently report success either way.
      const results: Record<string, { ok: boolean; message: string }> = {};
      for (const skuId of skuIds) {
        try {
          const priceRes = await billingApi.generatePrice(skuId);
          results[skuId] = priceRes.skipped
            ? { ok: false, message: priceRes.reason || 'No Stripe price was generated for this SKU — checkout will fail until one exists.' }
            : { ok: true, message: `Stripe price ${priceRes.priceId} ${priceRes.created ? 'generated' : 'already existed'} — this offer is sellable.` };
        } catch (e: any) {
          results[skuId] = { ok: false, message: e?.message || 'Failed to generate a Stripe price — checkout will fail until one is generated.' };
        }
      }
      setPriceResults(results);
    } catch (e: any) {
      setSaveError(e?.message || 'Failed to create the financing policy');
    } finally {
      setSaving(false);
    }
  };

  /**
   * BACK TO WHERE THIS STARTED.
   *
   * A device editor sends the operator here mid-flow — create the device, set
   * its terms, publish — and dropping them on the catalog at the end broke the
   * flow: they had to go and find the device again to publish it. `?back=device`
   * says "I came from the editor", so Done returns to it. Everything else keeps
   * the old destinations.
   */
  /** Opened from the device editor, mid-flow: create → terms → publish. */
  const cameFromDevice = searchParams.get('back') === 'device' && targetKind === 'model' && !!targetId;
  /** The device is still unpublished, so finishing here can finish the whole job. */
  const deviceIsDraft = model?.lifecycle?.status === 'draft';

  /**
   * PUBLISH FROM HERE.
   *
   * The terms were the only thing standing between this device and the
   * catalog, and they now exist — so the last step of the wizard is the
   * publish, not a trip back to the editor to press one more button. The row
   * is re-read before it is written so nothing on it is lost to a stale copy;
   * only `lifecycle.status` changes.
   */
  const publishDevice = async () => {
    setPublishBusy(true);
    setPublishError(null);
    try {
      const res = await hardwareApi.getModel(targetId);
      const m: any = (res as any)?.model || res;
      await hardwareApi.upsertModel({ ...m, lifecycle: { ...(m.lifecycle || {}), status: 'active' } });
      toastService.success(`${m.modelName || targetId} published`);
      navigate('/inventory/stock');
    } catch (e: any) {
      setPublishError(e?.message || 'Could not publish the device — its terms are saved, so it can be published from the device page.');
    } finally {
      setPublishBusy(false);
    }
  };

  const backTo = () => {
    if (searchParams.get('back') === 'device' && targetKind === 'model' && targetId) {
      navigate(`/inventory/stock/${encodeURIComponent(targetId)}/edit`);
      return;
    }
    navigate(bundle ? '/inventory/sales' : '/inventory/catalog');
  };

  if (loading) {
    return (
      <InventoryPage icon={CreditCard} title="Financing" subtitle="Loading…">
        <RubyLoader variant="page" />
      </InventoryPage>
    );
  }

  if (isEdit && seedError) {
    return (
      <InventoryPage icon={CreditCard} title="Edit policy">
        <div className="mx-auto max-w-lg rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-sm font-semibold text-amber-900">This policy could not be opened</p>
          <p className="mt-1 text-xs text-amber-800">{seedError}</p>
          <button
            onClick={() => navigate('/inventory/financing')}
            className="mt-4 rounded-lg bg-stone-800 px-4 py-2 text-xs font-semibold text-white hover:bg-stone-900"
          >
            Back to financing
          </button>
        </div>
      </InventoryPage>
    );
  }

  return (
    <InventoryPage
      icon={CreditCard}
      title={isEdit
        ? (targetName ? `Edit policy — ${targetName}` : 'Edit financing policy')
        : (targetName ? `New policy — ${targetName}` : 'New financing policy')}
    >
      {isEdit && !created && (
        <div className="mb-4 rounded-xl border border-stone-200 bg-white p-3.5">
          <p className="text-xs text-stone-600">
            <span className="font-semibold text-stone-800">Editing an existing policy.</span>{' '}
            Every mode and term below was loaded from this device&apos;s live offers, and each keeps its own SKU id.
            Removing a term or switching a mode off <span className="font-semibold">retires</span> that offer — agreements already running on it keep billing.
          </p>
          {seeded?.retiredCount ? (
            <p className="mt-1.5 text-[11px] text-amber-700">
              {seeded.retiredCount} retired offer{seeded.retiredCount === 1 ? ' was' : 's were'} loaded too — saving re-offers {seeded.retiredCount === 1 ? 'it' : 'them'}.
            </p>
          ) : null}
          {seeded?.unsupportedSkus.length ? (
            <p className="mt-1.5 text-[11px] text-stone-500">
              {seeded.unsupportedSkus.length} offer{seeded.unsupportedSkus.length === 1 ? '' : 's'} on this device
              ({seeded.unsupportedSkus.map((s) => s.saleMode || 'unset').join(', ')}) {seeded.unsupportedSkus.length === 1 ? 'is' : 'are'} not
              editable here and {seeded.unsupportedSkus.length === 1 ? 'is' : 'are'} left exactly as {seeded.unsupportedSkus.length === 1 ? 'it is' : 'they are'}.
            </p>
          ) : null}
        </div>
      )}
      {pendingDraft && !created && (() => {
        const draftBundle = pendingDraft.targetKind === 'bundle' ? bundles.find((b) => b.bundleId === pendingDraft.targetId) : undefined;
        const draftModel = pendingDraft.targetKind === 'model' ? models.find((m) => m.modelId === pendingDraft.targetId) : undefined;
        const draftName = draftBundle?.name || draftModel?.modelName || pendingDraft.targetId || 'no device picked yet';
        const stepLabel = ['', 'Device', 'Selling policy', 'Terms', 'Review'][pendingDraft.step] || `step ${pendingDraft.step}`;
        return (
          <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
            <History size={16} className="shrink-0 text-blue-600" />
            <div className="min-w-0 flex-1 text-xs text-blue-900">
              <span className="font-semibold">Unfinished draft:</span> {draftName} · at "{stepLabel}" ·
              saved {new Date(pendingDraft.savedAt).toLocaleString()}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={resumeDraft} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white" style={{ background: RUBY }}>
                Resume
              </button>
              <button onClick={discardDraft} className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700">
                Discard
              </button>
            </div>
          </div>
        );
      })()}
      <div className="flex items-center gap-3 mb-6 overflow-x-auto pb-1">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <WizStep n={s.n} label={s.label} current={step} />
            {i < steps.length - 1 && <div className="w-8 h-px bg-stone-300 shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      {/* THE DEVICE, ON EVERY STEP.
          Pricing decisions three steps deep are made against an abstract name
          otherwise — the operator loses sight of which physical thing they are
          pricing. Renders from step 2 onward: step 1 IS the picker and carries
          its own richer card, so repeating it there would be noise. */}
      {targetId && step > 1 && (
        <DeviceStrip
          name={targetName}
          subtitle={targetKind === 'bundle'
            ? `Bundle · ${bundle?.components?.length || 0} component${(bundle?.components?.length || 0) === 1 ? '' : 's'}`
            : [model?.manufacturer, model?.brand].filter(Boolean).join(' · ') || 'Device model'}
          imageUrl={bundle?.heroImageUrl || model?.media?.heroImageUrl}
          photos={(bundle?.photos || model?.media?.photos || []).filter(Boolean)}
          costCents={costCents}
          costLabel={includeOutbound ? 'Delivered cost' : 'Landed cost'}
        />
      )}

      {/* STEP 1 — Device */}
      {step === 1 && (
        <div className="max-w-xl">
          <h1 className="text-lg font-light text-stone-900 mb-1">Which device or bundle is this policy for?</h1>
          <p className="text-xs text-stone-500 mb-4">Pick a bundle or a single device model. Its landed cost feeds every margin figure ahead.</p>
          <SearchableDropdown
            options={deviceOptions}
            value={targetKind && targetId ? `${targetKind}:${targetId}` : null}
            onChange={(v) => {
              if (!v) { setTargetKind(''); setTargetId(''); return; }
              const [kind, id] = v.split(':') as ['bundle' | 'model', string];
              setTargetKind(kind); setTargetId(id);
            }}
            placeholder="-- Select a bundle or device --"
            searchPlaceholder="Search…"
            variant="light"
          />
          {targetId && (
            <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <ShimmerImage
                  src={bundle?.heroImageUrl || model?.media?.heroImageUrl}
                  alt={targetName}
                  width={72}
                  height={72}
                  objectFit="contain"
                  className="shrink-0 rounded-xl border border-stone-200 bg-stone-50"
                  fallbackBgColor="bg-stone-100"
                  fallbackIcon={<CreditCard size={22} className="text-stone-400" />}
                  variant="light"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-stone-900">{targetName}</div>
                  <div className="text-[11px] text-stone-500">
                    {targetKind === 'bundle'
                      ? `Bundle · ${bundle?.components?.length || 0} components`
                      : [model?.manufacturer, model?.brand].filter(Boolean).join(' · ') || 'Device model'}
                  </div>
                </div>
              </div>
              <CostBreakdown
                kind={targetKind === 'bundle' ? 'bundle' : 'model'}
                economics={model?.economics}
                landedCents={landedCents}
                outboundCents={outboundCents}
                includeOutbound={includeOutbound}
                onToggleOutbound={setIncludeOutbound}
              />
            </div>
          )}
        </div>
      )}

      {/* STEP 2 — Sale modes (toggles — any combination) */}
      {step === 2 && (
        <div>
          <h1 className="text-lg font-light text-stone-900 mb-1">How is the <b className="font-semibold">{targetName}</b> sold?</h1>
          <p className="text-xs text-stone-500 mb-4">
            Toggle every way a customer can get this device — each enabled mode becomes its own offer with its own terms.
          </p>
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl ${notSellable ? 'opacity-40 pointer-events-none' : ''}`}>
            {MODE_CARDS.map((p) => {
              const on = modes[p.id];
              return (
                <button
                  key={p.id}
                  onClick={() => setModes((prev) => ({ ...prev, [p.id]: !prev[p.id] }))}
                  className={`relative text-left rounded-2xl border p-4 transition ${
                    on ? 'shadow-sm' : 'border-stone-200 bg-white hover:border-stone-300'
                  }`}
                  style={on ? { borderColor: RUBY, background: '#fef2f4', boxShadow: `0 0 0 1px ${RUBY}` } : undefined}
                >
                  <div
                    className={`absolute top-3.5 right-3.5 w-5 h-5 rounded-full border flex items-center justify-center ${
                      on ? 'border-transparent text-white' : 'border-stone-300 text-transparent'
                    }`}
                    style={on ? { background: RUBY } : undefined}
                  >
                    <Check size={12} />
                  </div>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold mb-2 ${MODE_BADGE[p.id].cls}`}>{p.badge}</span>
                  <p.icon size={18} className="mb-2 block" style={{ color: on ? RUBY : '#78716c' }} />
                  <div className="text-sm font-bold text-stone-900">{p.name}</div>
                  <div className="mt-1 text-[10.5px] text-stone-500 leading-relaxed">{p.desc}</div>
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setNotSellable((v) => !v)}
            className={`mt-4 flex w-full max-w-4xl items-center gap-3 rounded-2xl border p-4 text-left transition ${
              notSellable ? 'border-amber-300 bg-amber-50' : 'border-stone-200 bg-white hover:border-stone-300'
            }`}
          >
            <div className={`relative h-5 w-9 shrink-0 rounded-full transition ${notSellable ? 'bg-amber-400' : 'bg-stone-200'}`}>
              <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${notSellable ? 'left-[18px]' : 'left-0.5'}`} />
            </div>
            <Ban size={16} className={notSellable ? 'text-amber-600' : 'text-stone-400'} />
            <div>
              <div className="text-sm font-bold text-stone-900">Not sellable</div>
              <div className="text-[10.5px] text-stone-500">
                Internal / provisioning stock only. Turning this on disables all three modes — no offers are created.
              </div>
            </div>
          </button>
        </div>
      )}

      {/* STEP 3 — Terms */}
      {step === 3 && (
        <div>
          <h1 className="text-lg font-light text-stone-900 mb-4">Terms for each enabled mode</h1>
          {!noOffer && targetId && (
            <div className="mb-4 max-w-sm rounded-xl border border-stone-200 bg-white p-3">
              <div className="text-[11px] font-semibold text-stone-900">
                {targetName} — what it costs us
              </div>
              <CostBreakdown
                kind={targetKind === 'bundle' ? 'bundle' : 'model'}
                economics={model?.economics}
                landedCents={landedCents}
                outboundCents={outboundCents}
                includeOutbound={includeOutbound}
                onToggleOutbound={setIncludeOutbound}
                compact
              />
            </div>
          )}
          {noOffer ? (
            <div className="rounded-xl border border-dashed border-stone-300 bg-white p-6 text-sm text-stone-500">
              {notSellable
                ? 'Not sellable — no terms to set. Continue to Review to finish (no offer will be created).'
                : 'No sale mode enabled — go back and toggle at least one, or mark the device Not sellable.'}
            </div>
          ) : (
            <div className={`grid grid-cols-1 gap-4 ${enabledModes.length >= 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
              {enabledModes.includes('outright') && (() => {
                const t = terms.outright;
                const retailCents = toCents(t.retailDollars);
                const directMargin = retailCents - costCents;
                const directPct = costCents > 0 ? Math.round((directMargin / costCents) * 100) : 0;
                return (
                  <div className="rounded-xl border border-stone-200 bg-white p-4">
                    <h4 className="text-xs font-bold text-stone-900 mb-3 flex items-center gap-2">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-100 text-blue-700">BUY</span>
                      Outright — cost and retail
                    </h4>
                    <div className="space-y-2.5">
                      <NumberField
                        label={includeOutbound ? 'Delivered cost (from device)' : 'Landed cost (from device)'}
                        value={(costCents / 100).toFixed(2)} onChange={() => {}} prefix="$" readOnly
                      />
                      <NumberField
                        label="Retail price" value={t.retailDollars} onChange={(v) => setMode('outright', { retailDollars: v })} prefix="$"
                        hint={retailCents > 0 && costCents > 0 ? `direct margin +${money(directMargin)} (${directPct}%)` : undefined}
                      />
                      <MarginPanel grossCents={retailCents} costCents={costCents} label="Customer pays" />
                      <NumberField label="Warranty (months)" value={t.warrantyMonths} onChange={(v) => setMode('outright', { warrantyMonths: v })} step="1" />
                    </div>
                  </div>
                );
              })()}

              {enabledModes.includes('installment') && (() => {
                const t = terms.installment;
                const setRow = (idx: number, patch: Partial<TermRow>) =>
                  setMode('installment', { termRows: t.termRows.map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
                const addRow = () => {
                  const last = toInt(t.termRows[t.termRows.length - 1]?.termMonths || '24', 24);
                  setMode('installment', { termRows: [...t.termRows, blankTermRow(String(last + 12))] });
                };
                const removeRow = (idx: number) =>
                  setMode('installment', { termRows: t.termRows.filter((_, i) => i !== idx) });
                const termsSeen = new Set<string>();
                return (
                  <div className="rounded-xl border border-stone-200 bg-white p-4">
                    <h4 className="text-xs font-bold text-stone-900 mb-1 flex items-center gap-2">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-100 text-rose-700">FINANCE</span>
                      Installments — owns at end
                    </h4>
                    <p className="mb-3 text-[10px] text-stone-400">
                      Offer several terms (24 / 36 / 60 / 72…) — each becomes its own offer with its own monthly, and
                      the customer picks one at checkout.
                    </p>
                    <div className="space-y-2.5">
                      {t.termRows.map((row, idx) => {
                        const monthly = toCents(row.monthlyDollars);
                        const term = toInt(row.termMonths);
                        const totalCents = monthly * term;
                        const paybackMonth = monthly > 0 && costCents > 0 ? Math.ceil(costCents / monthly) : null;
                        const dup = termsSeen.has(row.termMonths);
                        termsSeen.add(row.termMonths);
                        return (
                          <div key={idx} className={`rounded-lg border p-2.5 ${dup ? 'border-red-300 bg-red-50/50' : 'border-stone-200 bg-stone-50/60'}`}>
                            <div className="flex items-start gap-2">
                              <div className="grid flex-1 grid-cols-2 gap-2">
                                <NumberField label="Term (months)" value={row.termMonths} onChange={(v) => setRow(idx, { termMonths: v })} step="1" />
                                <NumberField
                                  label="Monthly" value={row.monthlyDollars} onChange={(v) => setRow(idx, { monthlyDollars: v })} prefix="$"
                                  hint={totalCents > 0 ? `= ${money(totalCents)} total${paybackMonth ? ` · payback mo ${paybackMonth}` : ''}` : undefined}
                                />
                                <NumberField label="Down payment" value={row.downPaymentDollars} onChange={(v) => setRow(idx, { downPaymentDollars: v })} prefix="$" />
                              </div>
                              {t.termRows.length > 1 && (
                                <button
                                  onClick={() => removeRow(idx)}
                                  className="mt-5 shrink-0 rounded-lg border border-stone-200 p-1.5 text-stone-400 hover:border-red-300 hover:text-red-500"
                                  title="Remove this term option"
                                >
                                  <X size={13} />
                                </button>
                              )}
                            </div>
                            {dup && <p className="mt-1 text-[10px] font-semibold text-red-600">Duplicate term — each option needs a different number of months.</p>}
                          </div>
                        );
                      })}
                      <button
                        onClick={addRow}
                        className="w-full rounded-lg border border-dashed border-stone-300 px-3 py-2 text-[11px] font-semibold text-stone-500 hover:border-stone-400 hover:text-stone-700"
                      >
                        + Add term option
                      </button>
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold text-stone-600">Early payoff</label>
                        <div className="rounded-lg border border-stone-200 bg-stone-50 px-2.5 py-1.5 text-xs text-stone-600">Remaining balance, no penalty</div>
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold text-stone-600">Grace (days)</label>
                        <input type="number" min="0" step="1" value={t.graceDays} onChange={(e) => setMode('installment', { graceDays: e.target.value })}
                          className="w-20 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-xs font-mono" />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold text-stone-600">On default</label>
                        <ActionBlockMultiSelect
                          value={t.enforcementActions ?? blocksForEnforcement(t.enforcement)}
                          onChange={(ids) => setMode('installment', { enforcementActions: ids })}
                        />
                      </div>
                      {(() => {
                        // Across ALL term options: an installment policy mints one SKU per
                        // row, so the honest single figure here is the FIRST row's term
                        // revenue. Every row's own margin is shown on Review.
                        const first = t.termRows[0];
                        const termGross = first ? toCents(first.monthlyDollars) * toInt(first.termMonths) : 0;
                        return (
                          <MarginPanel
                            grossCents={termGross} costCents={costCents}
                            label={first ? `Customer pays over ${first.termMonths || 0}mo` : 'Customer pays'}
                          />
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}

              {enabledModes.includes('rental') && (() => {
                const t = terms.rental;
                const monthly = toCents(t.retailDollars);
                const fixed = t.rentalKind === 'fixed_term';
                const term = fixed ? toInt(t.termMonths, 0) : 0;
                const paybackMonth = monthly > 0 && costCents > 0 ? Math.ceil(costCents / monthly) : null;
                const paybackPct = paybackMonth
                  ? (fixed && term ? Math.min(100, Math.round((paybackMonth / term) * 100)) : 100)
                  : 0;
                return (
                  <div className="rounded-xl border border-teal-200 bg-white p-4" style={{ boxShadow: '0 0 16px rgba(45,212,191,.08)' }}>
                    <h4 className="text-xs font-bold text-stone-900 mb-3 flex items-center gap-2">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-teal-100 text-teal-700">RENT</span>
                      Rental — device stays yours
                    </h4>
                    <div className="space-y-2.5">
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold text-stone-600">Rental kind</label>
                        <div className="flex gap-1 rounded-lg border border-stone-200 bg-stone-100 p-0.5">
                          {(['open_ended', 'fixed_term'] as RentalKind[]).map((k) => (
                            <button
                              key={k}
                              onClick={() => setMode('rental', { rentalKind: k })}
                              className={`flex-1 rounded-md px-2 py-1.5 text-[11px] font-semibold transition ${
                                t.rentalKind === k ? 'bg-white text-teal-700 shadow-sm' : 'text-stone-500'
                              }`}
                            >
                              {k === 'open_ended' ? 'Open-ended' : 'Fixed term'}
                            </button>
                          ))}
                        </div>
                        <p className="mt-1 text-[10px] text-stone-400">
                          {t.rentalKind === 'open_ended'
                            ? 'Bills monthly until cancelled. Device is returned on cancel.'
                            : 'Ends after the term — return or renew. Same D8-safe schedule as an installment.'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <NumberField
                          label="Monthly rent" value={t.retailDollars} onChange={(v) => setMode('rental', { retailDollars: v })} prefix="$"
                          hint={paybackMonth ? `payback at month ${paybackMonth}, then pure margin` : undefined}
                        />
                        {t.rentalKind === 'fixed_term' ? (
                          <NumberField label="Term (months)" value={t.termMonths} onChange={(v) => setMode('rental', { termMonths: v })} step="1" />
                        ) : (
                          <NumberField label="Min commitment (mo)" value={t.minCommitmentMonths} onChange={(v) => setMode('rental', { minCommitmentMonths: v })} step="1" hint="0 = cancel any time" />
                        )}
                      </div>
                      <NumberField
                        label="Security deposit" value={t.depositDollars} onChange={(v) => setMode('rental', { depositDollars: v })} prefix="$"
                        hint="one-time, refundable on clean return · 0 = none"
                      />
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold text-stone-600">Grace (days)</label>
                        <input type="number" min="0" step="1" value={t.graceDays} onChange={(e) => setMode('rental', { graceDays: e.target.value })}
                          className="w-20 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-xs font-mono" />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold text-stone-600">On default</label>
                        <ActionBlockMultiSelect
                          value={t.enforcementActions ?? blocksForEnforcement(t.enforcement)}
                          onChange={(ids) => setMode('rental', { enforcementActions: ids })}
                        />
                      </div>
                      <MarginPanel
                        grossCents={monthly}
                        costCents={costCents}
                        perMonth
                        label="Customer pays"
                        rental={{
                          paybackMonths: paybackMonth,
                          termMonths: fixed ? term : undefined,
                          minCommitmentMonths: fixed ? undefined : toInt(t.minCommitmentMonths, 0),
                        }}
                      />
                      <NumberField label="Warranty (months)" value={t.warrantyMonths} onChange={(v) => setMode('rental', { warrantyMonths: v })} step="1" hint="0 = covered for the whole rental" />
                      {paybackMonth && (
                        <div className="pt-1">
                          <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${paybackPct}%`, background: 'linear-gradient(90deg,#10b981,#2dd4bf)' }} />
                          </div>
                          <div className="mt-1 flex justify-between text-[9.5px] text-stone-400">
                            <span>cost {money(costCents)} recovered at month {paybackMonth}</span>
                            <span>{t.rentalKind === 'open_ended' ? 'every month after = margin' : `term ends at month ${term || '—'}`}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* STEP 4 — Review (was step 5 before the Agreement step was removed) */}
      {step === LAST_STEP && (
        <div className="max-w-3xl">
          <h1 className="text-lg font-light text-stone-900 mb-1">Review</h1>
          {created ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">
              <div className="flex items-center gap-2 font-semibold"><Check size={16} /> Financing policy created</div>
              <p className="mt-1 text-xs text-emerald-800">
                {created.length} offer{created.length === 1 ? '' : 's'} created. They now show on the bundle
                card, the deal editor's device picker, and Financing.
              </p>
              {/* W5 review I6 — per-SKU Stripe price status, never silent. */}
              <div className="mt-3 space-y-1.5">
                {created.map((skuId) => {
                  const r = priceResults[skuId];
                  return (
                    <div
                      key={skuId}
                      className={`flex items-start gap-2 rounded-lg border p-2 text-[11px] ${
                        r?.ok ? 'border-emerald-200 bg-white text-emerald-800' : 'border-amber-300 bg-amber-50 text-amber-900'
                      }`}
                    >
                      {r?.ok ? <Check size={13} className="mt-0.5 shrink-0" /> : <AlertTriangle size={13} className="mt-0.5 shrink-0" />}
                      <span className="min-w-0">
                        <span className="block text-[12px] font-semibold">{createdNames[skuId] || skuId}</span>
                        <span className="block">{r?.message || 'Stripe price status unknown — check the Offers tab.'}</span>
                        {/* The id still travels — it is what support and the
                            catalogue are keyed on — but underneath, small, as a
                            reference rather than as the headline. */}
                        <span className="mt-0.5 block font-mono text-[10px] opacity-60">{skuId}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* THE LAST STEP IS THE PUBLISH. The device was held back only
                  because it had no terms; they exist now, so finishing here
                  finishes the job rather than sending the operator back to
                  press one more button. */}
              {cameFromDevice && deviceIsDraft ? (
                <div className="mt-4">
                  {publishError && (
                    <p className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900">
                      {publishError}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={publishDevice}
                      disabled={publishBusy}
                      className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
                      style={{ background: RUBY }}
                    >
                      {publishBusy ? <RubyGemMark size={13} drawing color="currentColor" /> : <Check size={13} />}
                      {publishBusy ? 'Publishing…' : 'Publish device — done'}
                    </button>
                    <button
                      onClick={backTo}
                      className="rounded-lg border border-stone-300 bg-white px-4 py-2 text-xs font-semibold text-stone-700 hover:bg-stone-50"
                    >
                      Back to the device
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={backTo} className="mt-4 rounded-lg px-4 py-2 text-xs font-semibold text-white" style={{ background: RUBY }}>
                  {cameFromDevice ? 'Done — back to the device' : 'Done — back to Sales'}
                </button>
              )}
            </div>
          ) : noOffer ? (
            <div className="rounded-xl border border-dashed border-stone-300 bg-white p-6 text-sm text-stone-500">
              <p>
                {notSellable
                  ? <>This device is marked <b>Not sellable</b> — no offer will be created. It stays internal/provisioning stock only.</>
                  : <>No sale mode is enabled — no offer will be created. Go back to enable one, or mark the device Not sellable.</>}
              </p>
              <button onClick={backTo} className="mt-4 rounded-lg border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-700">Done</button>
            </div>
          ) : (
            <>
              <p className="text-xs text-stone-500 mb-4">
                One offer (SKU) is created per card below — each installment TERM is its own offer — carrying its
                price, term and enforcement policy. They appear instantly on the bundle card, the deal editor's
                device picker, and the Financing tab.
              </p>
              <div className="space-y-3">
                {enabledModes.flatMap((mode) => {
                  const t = terms[mode];
                  const recurringMode = mode !== 'outright';
                  // Installment: one review card per term option, mirroring the
                  // one-SKU-per-row buildSkuBodies actually creates.
                  const variants = mode === 'installment'
                    ? t.termRows.map((row) => ({
                        key: `installment-${row.termMonths}`,
                        label: `${row.termMonths}mo installment`,
                        retailCents: toCents(row.monthlyDollars),
                        downPaymentCents: toCents(row.downPaymentDollars),
                      }))
                    : [{
                        key: mode,
                        label: mode === 'outright' ? 'Outright'
                          : t.rentalKind === 'fixed_term' ? `${t.termMonths}mo rental` : 'Monthly rental (open-ended)',
                        retailCents: toCents(t.retailDollars),
                        downPaymentCents: 0,
                      }];
                  return variants.map((v) => (
                    <div key={v.key} className="rounded-xl border border-stone-200 bg-white p-4 text-xs">
                      <div className="flex items-center gap-2 font-bold text-stone-900 mb-2">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${MODE_BADGE[mode].cls}`}>
                          {MODE_BADGE[mode].label}
                        </span>
                        {targetName} — {v.label}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-1.5 gap-x-4 text-stone-600">
                        <div>{includeOutbound ? 'Delivered cost' : 'Landed cost'} <span className="block font-mono text-stone-900">{money(costCents)}</span></div>
                        <div>{mode === 'rental' ? 'Rent' : mode === 'installment' ? 'Monthly' : 'Retail'} <span className="block font-mono text-stone-900">{money(v.retailCents)}{recurringMode ? '/mo' : ''}</span></div>
                        <div>Warranty <span className="block font-mono text-stone-900">{t.warrantyMonths || 0} mo</span></div>
                        {(() => {
                          /* REVIEW MARGIN — what this offer earns on the device alone.
                             Installment shows the WHOLE term's revenue because that is
                             what the customer pays; rental is stated per month (a rental
                             has no closed total).

                             NO COMMISSION LINE (2026-08-30). Commission is the deal's,
                             and one offer is sold under many deals at many rates — a
                             single number here could only ever be one of them, which is
                             worse than no number. The deal editor shows what a partner
                             earns, against the deal that actually pays it. */
                          // `v.key` is `installment-<months>` (see the variants map above),
                          // so the term is the segment after the dash — not re-parsed from
                          // the human label, which is free text.
                          const variantTerm = mode === 'installment' ? toInt(v.key.split('-')[1]) : 0;
                          const variantGross = mode === 'installment'
                            ? v.retailCents * variantTerm
                            : v.retailCents;
                          /* A RENTAL IS RECOVERY, NOT MARGIN — one month's rent
                             minus the whole device is not a loss, it is month
                             one of a payback. Same correction as MarginPanel. */
                          if (mode === 'rental') {
                            const payback = v.retailCents > 0 && costCents > 0
                              ? Math.ceil(costCents / v.retailCents)
                              : null;
                            const rentalTerm = t.rentalKind === 'fixed_term' ? toInt(t.termMonths, 0) : 0;
                            const short = !!rentalTerm && !!payback && rentalTerm < payback;
                            return (
                              <>
                                <div>Revenue <span className="block font-mono text-stone-900">{money(v.retailCents)}/mo</span></div>
                                <div>
                                  Cost recovered
                                  <span className={`block font-mono ${short ? 'text-red-600 font-bold' : 'text-emerald-600'}`}>
                                    {payback ? `month ${payback}` : '—'}
                                    {short ? ` · term ends ${rentalTerm}` : ''}
                                  </span>
                                </div>
                              </>
                            );
                          }
                          const gm = variantGross - costCents;
                          return (
                            <>
                              <div>{mode === 'installment' ? 'Term revenue' : 'Revenue'} <span className="block font-mono text-stone-900">{money(variantGross)}</span></div>
                              <div>Gross margin <span className={`block font-mono ${gm < 0 ? 'text-red-600 font-bold' : 'text-emerald-600'}`}>{money(gm)}</span></div>
                            </>
                          );
                        })()}
                        {mode === 'installment' && (
                          <>
                            <div>Down payment <span className="block font-mono text-stone-900">{v.downPaymentCents > 0 ? money(v.downPaymentCents) : 'none'}</span></div>
                            <div>Default <span className="block font-mono text-stone-900">{t.graceDays}d → {blockLabels(t.enforcementActions ?? blocksForEnforcement(t.enforcement))}</span></div>
                            <div>Early payoff <span className="block font-mono text-stone-900">remaining, no penalty</span></div>
                          </>
                        )}
                        {mode === 'rental' && (
                          <>
                            <div>Default <span className="block font-mono text-stone-900">{t.graceDays}d → {blockLabels(t.enforcementActions ?? blocksForEnforcement(t.enforcement))}</span></div>
                            <div>Deposit <span className="block font-mono text-stone-900">{toCents(t.depositDollars) > 0 ? money(toCents(t.depositDollars)) : 'none'}</span></div>
                            <div>
                              {t.rentalKind === 'open_ended' ? 'Commitment' : 'Term'}
                              <span className="block font-mono text-stone-900">
                                {t.rentalKind === 'open_ended'
                                  ? (toInt(t.minCommitmentMonths) > 0 ? `${t.minCommitmentMonths} mo min` : 'cancel any time')
                                  : `${t.termMonths} mo, then return/renew`}
                              </span>
                            </div>
                          </>
                        )}
                        <div className="col-span-2 md:col-span-4">
                          Agreements &amp; commission
                          <span className="block text-stone-600">
                            Set on the <b>deal</b> that sells this offer — hardware and software paperwork, the
                            return policy, and what a partner earns. An offer carries none of its own.
                          </span>
                        </div>
                      </div>
                    </div>
                  ));
                })}
              </div>
              {/* EDIT: say EXACTLY what save will do — which offers update in
                  place, which are new, and which stop being offered. An
                  operator must never discover a retirement after the fact. */}
              {isEdit && (() => {
                const willWrite = enabledModes.flatMap((m) => buildSkuBodies(m));
                const updating = willWrite.filter((b) => seededSkuIds.includes(b.skuId));
                const creating = willWrite.filter((b) => !seededSkuIds.includes(b.skuId));
                const writtenIds = new Set(willWrite.map((b) => b.skuId));
                const retiring = seededSkuIds.filter((id) => !writtenIds.has(id));
                return (
                  <div className="mt-4 rounded-xl border border-stone-200 bg-stone-50 p-4 text-xs">
                    <p className="mb-2 font-bold text-stone-900">What saving does</p>
                    <ul className="space-y-1 text-stone-700">
                      <li>{updating.length} existing offer{updating.length === 1 ? '' : 's'} updated in place (same SKU id, agreements unaffected)</li>
                      <li>{creating.length} new offer{creating.length === 1 ? '' : 's'} created{creating.length ? ' — a Stripe price is minted for each' : ''}</li>
                      <li className={retiring.length ? 'font-semibold text-amber-800' : ''}>
                        {retiring.length} offer{retiring.length === 1 ? '' : 's'} retired
                        {retiring.length ? ' — stops being offered; agreements already on it keep billing' : ''}
                      </li>
                    </ul>
                    {retiring.length > 0 && (
                      <ul className="mt-2 space-y-0.5 font-mono text-[10px] text-stone-500">
                        {retiring.map((id) => <li key={id} className="break-all">{id}</li>)}
                      </ul>
                    )}
                  </div>
                );
              })()}
              {saveError && (
                <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {saveError}
                </div>
              )}
              <button
                onClick={handleCreate}
                disabled={saving}
                className="mt-4 flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` }}
              >
                {saving ? <RubyGemMark size={15} drawing color="currentColor" /> : <Check size={15} />}
                {isEdit ? 'Save policy changes' : 'Create financing policy'}
              </button>
            </>
          )}
        </div>
      )}

      {!created && (
        <div className="flex justify-between mt-8">
          <div className="flex items-center gap-2">
            <button
              // EDIT: step 2 is the first step; Back from there leaves the
              // wizard rather than dropping into the locked device picker.
              onClick={() => ((step === 1 || (isEdit && step === 2)) ? backTo() : setStep(step - 1))}
              className="flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-4 py-2 text-xs font-semibold text-stone-600 hover:bg-stone-50"
            >
              <ArrowLeft size={14} /> Back
            </button>
            {targetId && (
              <button
                onClick={backTo}
                className="rounded-lg px-3 py-2 text-xs font-semibold text-stone-500 hover:text-stone-700"
                title="Progress autosaves — resume from this exact step next time you open the wizard"
              >
                Save draft &amp; close
              </button>
            )}
          </div>
          {step < LAST_STEP && (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canContinue}
              className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-white disabled:opacity-40"
              style={{ background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` }}
            >
              Continue <ArrowRight size={14} />
            </button>
          )}
        </div>
      )}
    </InventoryPage>
  );
};

/**
 * THE DEVICE, PINNED TO EVERY STEP.
 *
 * From step 2 onward the operator is setting prices, terms and commission
 * against a name alone; the physical thing scrolls out of sight on step 1 and
 * never comes back. This keeps the picture, the identity and the cost in view
 * the whole way through, so a margin figure is always attached to something
 * concrete.
 *
 * Images go through ShimmerImage (never a raw <img>): device photos are
 * operator-uploaded and frequently missing, so the fallback icon and the
 * S3 -> CloudFront rewrite both matter here.
 */
const DeviceStrip: React.FC<{
  name: string;
  subtitle: string;
  imageUrl?: string;
  photos: string[];
  costCents: number;
  costLabel: string;
}> = ({ name, subtitle, imageUrl, photos, costCents, costLabel }) => {
  // The hero is usually also the first photo — showing it twice looks like a
  // duplicate upload rather than a gallery.
  const extras = photos.filter((u) => u && u !== imageUrl).slice(0, 4);
  return (
    <div className="mb-5 flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-3">
      <ShimmerImage
        src={imageUrl}
        alt={name}
        width={56}
        height={56}
        objectFit="contain"
        className="shrink-0 rounded-lg border border-stone-200 bg-stone-50"
        fallbackBgColor="bg-stone-100"
        fallbackIcon={<CreditCard size={20} className="text-stone-400" />}
        variant="light"
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-stone-900">{name}</div>
        <div className="truncate text-[11px] text-stone-500">{subtitle}</div>
      </div>

      {extras.length > 0 && (
        <div className="hidden items-center gap-1.5 sm:flex">
          {extras.map((url) => (
            <ShimmerImage
              key={url}
              src={url}
              alt={name}
              width={34}
              height={34}
              objectFit="contain"
              className="rounded-md border border-stone-200 bg-stone-50"
              fallbackBgColor="bg-stone-100"
              fallbackIcon={<CreditCard size={12} className="text-stone-300" />}
              variant="light"
            />
          ))}
        </div>
      )}

      <div className="shrink-0 border-l border-stone-200 pl-3 text-right">
        <div className="text-[9.5px] font-semibold uppercase tracking-wider text-stone-400">{costLabel}</div>
        <div className="font-mono text-sm font-semibold tabular-nums text-stone-900">{money(costCents)}</div>
      </div>
    </div>
  );
};

/**
 * WHAT THIS DEVICE COSTS US, ITEMISED.
 *
 * Reads straight off the rows already loaded (`model.economics` /
 * the bundle's server-derived component + outbound figures), so it costs no
 * extra request. Outbound sits BELOW the landed subtotal because it is a
 * per-shipment cost, not a per-unit one; the toggle folds it in for operators
 * who want to price against delivered cost instead.
 */
const CostBreakdown: React.FC<{
  kind: 'bundle' | 'model';
  economics?: DeviceModelRow['economics'];
  landedCents: number;
  outboundCents: number;
  includeOutbound: boolean;
  onToggleOutbound: (next: boolean) => void;
  compact?: boolean;
}> = ({ kind, economics, landedCents, outboundCents, includeOutbound, onToggleOutbound, compact }) => {
  const Row: React.FC<{ label: string; cents: number; strong?: boolean; muted?: boolean }> = ({ label, cents, strong, muted }) => (
    <div className={`flex items-baseline justify-between ${strong ? 'font-semibold text-stone-900' : muted ? 'text-stone-400' : 'text-stone-600'}`}>
      <span>{label}</span>
      <span className="font-mono tabular-nums">{money(cents)}</span>
    </div>
  );
  return (
    <div className={`text-[11px] ${compact ? '' : 'mt-3'}`}>
      <div className="space-y-1">
        {kind === 'model' && economics ? (
          <>
            <Row label="Unit cost" cents={economics.unitCostCents} />
            <Row label="+ Inbound shipping" cents={economics.inboundShippingCents} />
            <Row label="+ Duty" cents={economics.dutyCents} />
          </>
        ) : (
          <Row label="Components (landed)" cents={landedCents} />
        )}
        <div className="border-t border-stone-200 pt-1">
          <Row label="= Landed cost / unit" cents={landedCents} strong={!includeOutbound} />
        </div>
        <Row
          label={`${includeOutbound ? '+' : ' '} Outbound shipping`}
          cents={outboundCents}
          muted={!includeOutbound}
        />
        {includeOutbound && (
          <div className="border-t border-stone-200 pt-1">
            <Row label="= Delivered cost / unit" cents={landedCents + outboundCents} strong />
          </div>
        )}
      </div>
      <label className="mt-2 flex cursor-pointer items-center gap-1.5 text-[10px] text-stone-500">
        <input
          type="checkbox"
          checked={includeOutbound}
          onChange={(e) => onToggleOutbound(e.target.checked)}
          className="h-3 w-3 accent-rose-500"
        />
        Count outbound shipping in margin
      </label>
      <p className="mt-1 text-[10px] text-stone-400">
        Outbound is charged per SHIPMENT, not per unit, so it is out of landed cost by default. This toggle
        only changes the figures shown — nothing about it is saved on the offer.
      </p>
    </div>
  );
};

/**
 * THE P&L FOR ONE OFFER — margin on the device, and nothing else.
 *
 * WAS `CommissionPanel` (removed 2026-08-30). It carried a commission plan
 * picker, an inline "add a rule for this device" form, and a margin figure net
 * of commission. All three are gone because commission is no longer a property
 * of an offer: it is the DEAL's, set once on the deal editor, where a partner's
 * cut belongs. One offer sells under many deals at many rates, so a per-offer
 * commission number could only ever be one of them.
 *
 * `grossCents` is what the customer pays across the whole offer: the one-time
 * price for BUY, monthly x term for FINANCE, and one month for RENT (a rental
 * has no closed total, so its margin is stated per month and labelled as such
 * rather than pretending to a lifetime figure).
 */
const MarginPanel: React.FC<{
  grossCents: number;
  costCents: number;
  /** RENT / FINANCE monthly framing for the labels. */
  perMonth?: boolean;
  label?: string;
  /**
   * RENTAL FRAMING. A rent of $14.99 against a $180 device is not a $165 loss
   * — it is a device that pays for itself in 13 months and earns $14.99 every
   * month after that. Subtracting the whole landed cost from ONE month's rent
   * produced a headline loss and a "-1101%" on every healthy rental, which is
   * arithmetic answering a question nobody asked. Given this, the panel states
   * recovery and post-payback profit instead.
   */
  rental?: {
    paybackMonths: number | null;
    /** Fixed-term rentals only — the term the offer actually runs for. */
    termMonths?: number;
    minCommitmentMonths?: number;
  };
}> = ({ grossCents, costCents, perMonth, label, rental }) => {
  const grossMargin = grossCents - costCents;
  const marginPct = grossCents > 0 ? Math.round((grossMargin / grossCents) * 100) : null;
  const suffix = perMonth ? '/mo' : '';

  if (rental) {
    const { paybackMonths, termMonths, minCommitmentMonths } = rental;
    /**
     * A FIXED TERM CAN GENUINELY LOSE MONEY: if it ends before the device is
     * paid off, the whole term's rent never covers the cost. That — not a
     * single month's arithmetic — is what deserves the red warning.
     */
    const termTotal = termMonths ? grossCents * termMonths : null;
    const termNet = termTotal != null ? termTotal - costCents : null;
    const shortTerm = !!termMonths && !!paybackMonths && termMonths < paybackMonths;
    // Cancellable before payback: not a loss (the device comes back and rents
    // again), but the operator should see it.
    const shortCommitment = !termMonths && !!paybackMonths && !!minCommitmentMonths
      && minCommitmentMonths > 0 && minCommitmentMonths < paybackMonths;

    return (
      <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-2.5">
        <div className="space-y-1 text-[11px]">
          <Line label={`${label || 'Customer pays'}/mo`} cents={grossCents} />
          <Line label="Landed cost to recover" cents={costCents} />
          <div className="border-t border-stone-200 pt-1">
            <div className="flex items-baseline justify-between font-semibold">
              <span className="text-stone-900">Cost recovered</span>
              <span className="font-mono tabular-nums text-stone-900">
                {paybackMonths ? `month ${paybackMonths}` : '—'}
              </span>
            </div>
            <div className="mt-0.5 flex items-baseline justify-between font-semibold">
              <span className="text-stone-900">Pure profit after that</span>
              <span className="font-mono tabular-nums text-emerald-600">{money(grossCents)}/mo</span>
            </div>
            {termTotal != null && (
              <div className="mt-0.5 flex items-baseline justify-between text-stone-600">
                <span>Net over the {termMonths}-month term</span>
                <span className={`font-mono tabular-nums ${(termNet || 0) < 0 ? 'text-red-600 font-semibold' : 'text-emerald-600'}`}>
                  {money(termNet || 0)}
                </span>
              </div>
            )}
            {grossCents <= 0 ? (
              <p className="mt-1 text-[10px] font-semibold text-red-600">
                No rent set — this offer earns nothing.
              </p>
            ) : shortTerm ? (
              <p className="mt-1 text-[10px] font-semibold text-red-600">
                The term ends at month {termMonths}, before the device pays for itself at month {paybackMonths}.
              </p>
            ) : shortCommitment ? (
              <p className="mt-1 text-[10px] text-amber-700">
                Cancellable from month {minCommitmentMonths}, before the device pays for itself at month {paybackMonths}
                {' '}— the device comes back and rents again, but this one contract may not cover it.
              </p>
            ) : null}
          </div>
        </div>

        <p className="mt-1.5 text-[10px] text-stone-400">
          A rental never converts to a sale, so there is no lifetime margin to state — the device is
          recovered over the months above and every month after is margin.
          {' '}Before partner commission, which is set on the deal.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-2.5">
      <div className="space-y-1 text-[11px]">
        <Line label={`${label || 'Customer pays'}${suffix}`} cents={grossCents} />
        <Line label="Landed cost" cents={-costCents} />
        <div className="border-t border-stone-200 pt-1">
          <div className="flex items-baseline justify-between font-semibold">
            <span className={grossMargin < 0 ? 'text-red-600' : 'text-stone-900'}>Gross margin{suffix}</span>
            <span className={`font-mono tabular-nums ${grossMargin < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {money(grossMargin)}{marginPct != null ? ` · ${marginPct}%` : ''}
            </span>
          </div>
          {grossMargin < 0 && (
            <p className="mt-1 text-[10px] font-semibold text-red-600">
              This offer sells below what the device costs.
            </p>
          )}
        </div>
      </div>

      <p className="mt-1.5 text-[10px] text-stone-400">
        Before partner commission, which is set on the deal.
      </p>
    </div>
  );
};

/** One money row in the breakdown. Negatives render with their own sign. */
const Line: React.FC<{ label: string; cents: number; strong?: boolean }> = ({ label, cents, strong }) => (
  <div className={`flex items-baseline justify-between ${strong ? 'font-semibold text-stone-900' : 'text-stone-600'}`}>
    <span className="pr-2">{label}</span>
    <span className="font-mono tabular-nums">{cents < 0 ? `−${money(Math.abs(cents))}` : money(cents)}</span>
  </div>
);

export default FinancingWizardPage;
